import { useMutation, useQueryClient } from '@tanstack/react-query';
import UsersAPIServiceV2 from './UserServiceV2';
import { USERS_QUERY_KEYS } from './usersQuery';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useTranslation } from 'react-i18next';
import {
    CreateCompanyRequest,
    CreateUserRequest,
    UpdateCompanyAuthSettingsRequest,
    UpdateCompanyRequest,
    UpdateConsentsRequest,
    UpdateUserRequest,
    UpdateUserRoleRequest,
    UploadCompanyLogoRequest,
    ChangeUsersStatusRequest,
    CreateUsersInMultiCompaniesRequest
} from '@/models/company';

const usersService = UsersAPIServiceV2.getInstance();

export const useUsersMutations = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useNotificationsSnackbar();
    const { t } = useTranslation();

    const sendActivationEmail = useMutation({
        mutationFn: (userId: string) => usersService.sendActivationEmail(userId)
    });

    const createUser = useMutation({
        mutationFn: (user: CreateUserRequest) => usersService.createUser(user),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [USERS_QUERY_KEYS.getCompanies]
            });
        }
    });

    const updateUserRole = useMutation({
        mutationFn: (request: UpdateUserRoleRequest) =>
            usersService.updateUserRole(request)
    });

    const updateCurrentCompany = useMutation({
        mutationFn: (request: UpdateCompanyRequest) =>
            usersService.updateCurrentCompany(request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [USERS_QUERY_KEYS.getCurrentCompany]
            });
        }
    });

    const updateUser = useMutation({
        mutationFn: (user: UpdateUserRequest) => usersService.updateUser(user)
    });

    const uploadCompanyLogo = useMutation({
        mutationFn: (request: UploadCompanyLogoRequest) =>
            usersService.uploadCompanyLogo(request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [USERS_QUERY_KEYS.getCurrentCompany]
            });
        }
    });

    const updateConsents = useMutation({
        mutationFn: (request: UpdateConsentsRequest) =>
            usersService.updateConsents(request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [USERS_QUERY_KEYS.getConsents]
            });
        }
    });

    const postponeConsents = useMutation({
        mutationFn: () => usersService.postponeConsents(),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [USERS_QUERY_KEYS.getConsents]
            });
        }
    });

    const updateCurrentAuthSettings = useMutation({
        mutationFn: (request: UpdateCompanyAuthSettingsRequest) =>
            usersService.updateCurrentAuthSettings(request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [USERS_QUERY_KEYS.getCurrentCompany]
            });
        }
    });

    const createCompany = useMutation({
        mutationFn: (company: CreateCompanyRequest) =>
            usersService.createCompany(company),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [USERS_QUERY_KEYS.getCompanies]
            });
        }
    });

    const deleteUser = useMutation({
        mutationFn: (userId: string) => usersService.deleteUser(userId)
    });

    const disableUsers = useMutation({
        mutationFn: ({
            request,
            companyId
        }: {
            request: ChangeUsersStatusRequest;
            companyId?: string;
        }) => usersService.disableUsers(request, companyId)
    });

    const createUserInMultiCompanies = useMutation({
        mutationFn: (request: CreateUsersInMultiCompaniesRequest) =>
            usersService.createUserInMultiCompanies(request),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [USERS_QUERY_KEYS.getCompanies]
            });
        }
    });

    const uploadLegalDocument = useMutation({
        mutationFn: (formData: FormData) =>
            usersService.uploadLegalDocument(formData),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [USERS_QUERY_KEYS.getConsents]
            });
            setAlert(t('Document uploaded successfully'), 'success');
        },
        onError: (error: any) => {
            setAlert(error.message || t('Could not upload document'), 'error');
        }
    });

    return {
        sendActivationEmail,
        createUser,
        updateUserRole,
        updateCurrentCompany,
        updateUser,
        uploadCompanyLogo,
        updateConsents,
        postponeConsents,
        updateCurrentAuthSettings,
        createCompany,
        deleteUser,
        disableUsers,
        createUserInMultiCompanies,
        uploadLegalDocument
    };
};
